import { ConsolidatedLocationView, LocationSourceEnum } from '@mark43/cad-api';
import { GeoPoint, LocationStoreView } from '../../location/types';

/**
 * get the closet location which has minimal reverseGeocodeDistance value
 *
 * reverseGeocodeDistance may not exist in LocationStoreView, so
 * if latLngToCompare is provided (prefer LAT_LONG result), return the location that has exact lat/lng matches.
 * (meaning the query may be a `lat,lng` string, find result that has resolverSource = LocationSourceEnum.LAT_LONG.name AND lat/lng first)
 *    if no LAT_LONG returned from BE, return the closet location.
 * if latLngToCompare is not provided, return the closet location.
 *
 * if the closet location is not found (all results have no reverseGeocodeDistance field), return latLngLocation, if any.
 * if still couldn't find a location, return first element from results
 */

function isEsriLocation(location: LocationStoreView | ConsolidatedLocationView): boolean {
    // Prioritize non-ESRI locations (like THP mile markers) over ESRI generic locations
    return location.source === LocationSourceEnum.ESRI.name || 
           location.source === LocationSourceEnum.ESRI_CUSTOM.name;
}

export function getClosestLocation(
    locationSearchResults?: (LocationStoreView | ConsolidatedLocationView)[],
    latLngToCompare?: GeoPoint
): LocationStoreView | ConsolidatedLocationView | undefined {
    if (!locationSearchResults || !locationSearchResults.length) {
        return;
    }
    if (locationSearchResults.length === 1) {
        return locationSearchResults[0];
    }
    let minDistance = Number.MAX_SAFE_INTEGER;
    let latLngLocation: LocationStoreView | ConsolidatedLocationView | undefined;
    let closestLocation: LocationStoreView | ConsolidatedLocationView | undefined;
    let closestNonEsriLocation: LocationStoreView | ConsolidatedLocationView | undefined;
    let nonEsriMinDistance = Number.MAX_SAFE_INTEGER;
    
    for (const location of locationSearchResults) {
        if (
            latLngToCompare &&
            latLngToCompare.latitude === location.latitude &&
            latLngToCompare.longitude === location.longitude
        ) {
            return location;
        }
        // there will be at most one LAT_LONG location returned from BE
        if (location.resolverSource === LocationSourceEnum.LAT_LONG.name) {
            latLngLocation = location;
        } else {
            if (!isEsriLocation(location)) {
                const distance = location.reverseGeocodeDistance ?? Number.MAX_SAFE_INTEGER;
                if (distance < nonEsriMinDistance) {
                    nonEsriMinDistance = distance;
                    closestNonEsriLocation = location;
                }
            }
            else if (
                typeof location.reverseGeocodeDistance === 'number' &&
                location.reverseGeocodeDistance <= minDistance
            ) {
                minDistance = location.reverseGeocodeDistance;
                closestLocation = location;
            }
        }
    }
    
    if (closestNonEsriLocation) {
        return closestNonEsriLocation;
    }
    
    if (closestLocation && minDistance <= Number.MAX_SAFE_INTEGER) {
        return closestLocation;
    }
    if (latLngLocation) {
        return latLngLocation;
    }
    return locationSearchResults[0];
}
